import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import axios, { AxiosError, AxiosInstance } from 'axios';
import { notifications } from '@mantine/notifications';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';
import {
  getSignedUploadConfig,
  normalizeUploadContentType,
  relativeFileToUploadMetadata,
} from '@/hooks/api/datasets/uploadMetadata.ts';
import {
  ManualDatasetCreateResponse,
  ManualDirectoryDatasetCreateRequest,
  ManualNonDirectoryDatasetCreateRequest,
} from '@/hooks/api/datasets/static/types.ts';
import { RelativeFile } from '@/types/common.ts';
import { StaticEditorReplacement } from '@/types/datasets.ts';
import { useEditVersionContext } from '@/context/EditVersionContext/useEditVersionContext.ts';
import {
  get_static_dataset_replacement_directory_url,
  get_static_dataset_replacement_file_url,
} from '@/utils/urls.ts';
import { STATIC_DATASETS_KEY, STATIC_DATASET_USAGE_KEY } from '@/utils/queryKeys.ts';
import useStaticUploadOperation from './useStaticUploadOperation.ts';
import { useRef } from 'react';

type ReplaceStaticDatasetProps = {
  staticDatasetId: string;
  name: string;
  files: RelativeFile[];
};

const useReplaceStaticDataset = (
  options?: UseMutationOptions<StaticEditorReplacement, AxiosError, ReplaceStaticDatasetProps>,
): UseMutationResult<StaticEditorReplacement, AxiosError, ReplaceStaticDatasetProps> => {
  const apiClient = useApiClient();
  const automationId = useAutomationId();
  const queryClient = useQueryClient();
  const runUpload = useStaticUploadOperation<StaticEditorReplacement>();
  const editor = useEditVersionContext();
  const commitVersions = useRef<Record<string, string>>({});

  return useMutation({
    mutationFn: async ({ staticDatasetId, name, files }: ReplaceStaticDatasetProps) => {
      if (!editor.editingVersion || editor.hasPendingEdits || editor.staleDraft)
        throw new Error('Wait for edits to save or reload the editor before replacing data.');
      const expectedVersionId = editor.editingVersion.id;
      if (files.length === 0) {
        throw new Error('At least one file is required to replace a static dataset');
      }

      const isSingleFile = files.length === 1;
      const filesToUpload = isSingleFile
        ? files
        : files.filter((relativeFile) => !relativeFile.file.name.endsWith('.DS_Store'));

      if (filesToUpload.length === 0) {
        throw new Error('At least one supported file is required to replace a static dataset');
      }

      const operation = await runUpload({
        identity: JSON.stringify([automationId, staticDatasetId, name]),
        files: filesToUpload,
        prepare: async () =>
          (isSingleFile
            ? await prepareFileReplacement(apiClient, staticDatasetId, name, filesToUpload[0].file)
            : await prepareDirectoryReplacement(apiClient, staticDatasetId, name, filesToUpload)
          ).data,
        upload: (prepared) => uploadReplacementFiles(prepared, filesToUpload),
        commit: async (prepared) => {
          commitVersions.current[prepared.dataset.id] ??= expectedVersionId;
          return (
            await apiClient.post<StaticEditorReplacement>(
              `/datasets/static/${staticDatasetId}/editor-replacements`,
              {
                dataset_id: prepared.dataset.id,
                expected_version_id: commitVersions.current[prepared.dataset.id],
                request_id: `replace:${prepared.dataset.id}`,
              },
            )
          ).data;
        },
      });
      editor.registerReplacement?.(operation.id);
      void queryClient.invalidateQueries({ queryKey: STATIC_DATASETS_KEY(automationId) });
      void queryClient.invalidateQueries({ queryKey: STATIC_DATASET_USAGE_KEY(automationId) });

      return operation;
    },
    ...options,
  });
};

const prepareFileReplacement = (
  apiClient: AxiosInstance,
  staticDatasetId: string,
  name: string,
  file: File,
) => {
  const postData: ManualNonDirectoryDatasetCreateRequest = {
    name,
    path: file.name,
    content_type: file.type ? normalizeUploadContentType(file.type) : null,
  };

  return apiClient.post<ManualDatasetCreateResponse>(
    get_static_dataset_replacement_file_url(staticDatasetId),
    postData,
  );
};

const prepareDirectoryReplacement = (
  apiClient: AxiosInstance,
  staticDatasetId: string,
  name: string,
  files: RelativeFile[],
) => {
  const postData: ManualDirectoryDatasetCreateRequest = {
    name,
    files: files.map(relativeFileToUploadMetadata),
  };

  return apiClient.post<ManualDatasetCreateResponse>(
    get_static_dataset_replacement_directory_url(staticDatasetId),
    postData,
  );
};

const uploadReplacementFiles = async (
  response: ManualDatasetCreateResponse,
  files: RelativeFile[],
) => {
  const unsupportedFiles = response.unsupported_files;

  if (unsupportedFiles.length > 0) {
    const fileTypes = unsupportedFiles.map((file) => file.split('.').pop());
    const uniqueFileTypes = Array.from(new Set(fileTypes));
    notifications.show({
      title: `Skipping ${unsupportedFiles.length} unsupported files`,
      message: `Skipped unsupported file types: ${uniqueFileTypes.join(', ')}`,
      color: 'yellow',
    });
  }

  if (files.length === 1) {
    const file = files[0].file;
    const presignedUrl = response.urls[response.dataset.name] ?? Object.values(response.urls)[0];
    const contentType = file.type ? normalizeUploadContentType(file.type) : null;
    await axios.put(presignedUrl, file, getSignedUploadConfig(contentType));
    return;
  }

  const uploadPromises: Promise<unknown>[] = [];
  for (const relativeFile of files) {
    const uploadMetadata = relativeFileToUploadMetadata(relativeFile);
    if (unsupportedFiles.includes(uploadMetadata.path)) {
      continue;
    }

    const url = response.urls[uploadMetadata.path];
    if (!url) {
      throw new Error(`Missing upload URL for ${uploadMetadata.path}`);
    }

    uploadPromises.push(
      axios.put(url, relativeFile.file, getSignedUploadConfig(uploadMetadata.content_type)),
    );
  }

  await Promise.all(uploadPromises);
};

export default useReplaceStaticDataset;
